import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Camera, FileText, Building, Users, User } from 'lucide-react';
import { toast } from 'sonner';

const AddEmployeeModal = ({
  showAddModal,
  setShowAddModal,
  formData,
  setFormData,
  handleAddEmployee,
  handleFileUpload,
  branches,
  positions,
  professions,
  selectedSupports,
  setSelectedSupports,
  selectedCompany,
  setSelectedCompany,
  subcontractors
}) => {
  return (
    <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="add-employee-modal">
        <DialogHeader className="flex flex-row justify-between items-start pr-8">
          <div>
            <DialogTitle>Yeni Çalışan Ekle</DialogTitle>
            <DialogDescription>Yeni çalışan bilgilerini girin</DialogDescription>
          </div>
          {typeof handleOpenRehireList === 'function' && (
            <Button type="button" variant="outline" size="sm" onClick={handleOpenRehireList} className="text-blue-600 border-blue-200 hover:bg-blue-50">
              <User className="h-4 w-4 mr-2" />
              Eski Çalışanlardan Seç
            </Button>
          )}
        </DialogHeader>
        <form onSubmit={handleAddEmployee}>
          {/* Company/Subcontractor Selection */}
          <div className="mb-4 p-4 bg-[hsl(var(--muted))] rounded-lg border border-[hsl(var(--border))]">
            <Label className="text-sm font-semibold mb-2 block">Şirket / Taşeron Seçimi *</Label>
            <Select 
              value={selectedCompany} 
              onValueChange={setSelectedCompany}
            >
              <SelectTrigger className="w-full bg-white" data-testid="select-company">
                <SelectValue placeholder="Şirket seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fortis">
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-blue-600" />
                    <span className="font-medium">FORTİS</span>
                  </div>
                </SelectItem>
                {subcontractors.map(sub => (
                  <SelectItem key={sub.id} value={sub.id}>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-orange-600" />
                      <span>{sub.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">
              Çalışan <strong>{selectedCompany === 'fortis' ? 'FORTİS' : subcontractors.find(s => s.id === selectedCompany)?.name || 'Seçili Şirket'}</strong> kadrosuna eklenecektir.
            </p>
          </div>
          
          {/* Photo Upload Section */}
          <div className="flex flex-col items-center py-4 border-b border-[hsl(var(--border))] mb-4">
            <div className="w-32 h-32 rounded-full border-2 border-[hsl(var(--border))] overflow-hidden bg-[hsl(var(--muted))] flex items-center justify-center mb-3">
              {formData.photo_url ? (
                <img src={formData.photo_url} alt="Çalışan fotoğrafı" className="w-full h-full object-cover" />
              ) : (
                <User className="h-16 w-16 text-[hsl(var(--muted-foreground))]" />
              )}
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileUpload(e, 'photo')}
              className="hidden"
              id="photo-upload"
            />
            <label htmlFor="photo-upload">
              <Button type="button" variant="outline" size="sm" asChild>
                <span className="cursor-pointer">Fotoğraf Seç</span>
              </Button>
            </label>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
              Maksimum 5MB (JPG, PNG)
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            {/* Personal Info */}
            <div className="col-span-2">
              <h3 className="text-sm font-semibold mb-2">Kişisel Bilgiler</h3>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tc_no">TC Kimlik No *</Label>
              <Input
                id="tc_no"
                value={formData.tc_no}
                onChange={(e) => setFormData({ ...formData, tc_no: e.target.value })}
                required
                maxLength={11}
                data-testid="input-tc-no"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Ad Soyad *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="input-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-mail Adresi</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="ornek@fortis.com"
                data-testid="input-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="birth_date">Doğum Tarihi</Label>
              <Input
                id="birth_date"
                type="date"
                value={formData.birth_date}
                onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                data-testid="input-birth-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gender">Cinsiyet</Label>
              <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                <SelectTrigger data-testid="input-gender">
                  <SelectValue placeholder="Seçiniz" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="erkek">Erkek</SelectItem>
                  <SelectItem value="kadın">Kadın</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="marital_status">Medeni Durum</Label>
              <Select value={formData.marital_status} onValueChange={(value) => setFormData({ ...formData, marital_status: value })}>
                <SelectTrigger data-testid="input-marital-status">
                  <SelectValue placeholder="Seçiniz" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bekar">Bekar</SelectItem>
                  <SelectItem value="evli">Evli</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="children_count">Çocuk Sayısı</Label>
              <Input
                id="children_count"
                type="number"
                min="0"
                value={formData.children_count}
                onChange={(e) => setFormData({ ...formData, children_count: parseInt(e.target.value) || 0 })}
                data-testid="input-children-count"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="education_level">Öğrenim Durumu</Label>
              <Select value={formData.education_level} onValueChange={(value) => setFormData({ ...formData, education_level: value })}>
                <SelectTrigger data-testid="input-education">
                  <SelectValue placeholder="Seçiniz" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ilkokul">İlkokul</SelectItem>
                  <SelectItem value="ortaokul">Ortaokul</SelectItem>
                  <SelectItem value="lise">Lise</SelectItem>
                  <SelectItem value="universite">Üniversite</SelectItem>
                  <SelectItem value="yuksek_lisans">Yüksek Lisans</SelectItem>
                  <SelectItem value="doktora">Doktora</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="profession">Meslek</Label>
              <Select
                value={formData.profession}
                onValueChange={(value) => setFormData({ ...formData, profession: value })}
              >
                <SelectTrigger id="profession" data-testid="select-profession">
                  <SelectValue placeholder="Meslek seçin..." />
                </SelectTrigger>
                <SelectContent>
                  {professions.filter((p, i, arr) => arr.findIndex(x => x.name === p.name) === i).map((prof) => (
                    <SelectItem key={prof.id} value={prof.name}>
                      {prof.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Telefon *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                data-testid="input-phone"
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="address">Adres</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                data-testid="input-address"
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="emergency_contact">Acil Durum İletişim</Label>
              <Input
                id="emergency_contact"
                value={formData.emergency_contact}
                onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                data-testid="input-emergency-contact"
              />
            </div>

            {/* Sağlık Bilgileri */}
            <div className="col-span-2 mt-4">
              <h3 className="text-sm font-semibold mb-2 text-red-700">Sağlık Bilgileri</h3>
            </div>
            <div className="space-y-2">
              <Label htmlFor="blood_type">Kan Grubu</Label>
              <Select value={formData.blood_type} onValueChange={(value) => setFormData({ ...formData, blood_type: value })}>
                <SelectTrigger data-testid="select-blood-type">
                  <SelectValue placeholder="Kan grubu seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A+">A Rh+</SelectItem>
                  <SelectItem value="A-">A Rh-</SelectItem>
                  <SelectItem value="B+">B Rh+</SelectItem>
                  <SelectItem value="B-">B Rh-</SelectItem>
                  <SelectItem value="AB+">AB Rh+</SelectItem>
                  <SelectItem value="AB-">AB Rh-</SelectItem>
                  <SelectItem value="0+">0 Rh+</SelectItem>
                  <SelectItem value="0-">0 Rh-</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="chronic_diseases">Kronik Rahatsızlıklar</Label>
              <Input
                id="chronic_diseases"
                value={formData.chronic_diseases}
                onChange={(e) => setFormData({ ...formData, chronic_diseases: e.target.value })}
                placeholder="Varsa belirtin"
                data-testid="input-chronic-diseases"
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="allergies">Alerjenler</Label>
              <Input
                id="allergies"
                value={formData.allergies}
                onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                placeholder="Varsa belirtin (ilaç, gıda, vb.)"
                data-testid="input-allergies"
              />
            </div>

            {/* Employment Info */}
            <div className="col-span-2 mt-4">
              <h3 className="text-sm font-semibold mb-2">İş Bilgileri</h3>
            </div>
            <div className="space-y-2">
              <Label htmlFor="hire_date">İşe Giriş Tarihi *</Label>
              <Input
                id="hire_date"
                type="date"
                value={formData.hire_date}
                onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                required
                data-testid="input-hire-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="department">Departman</Label>
              <Input
                id="department"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                data-testid="input-department"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="position_id">Görev *</Label>
              <Select value={formData.position_id} onValueChange={(value) => setFormData({ ...formData, position_id: value })} required>
                <SelectTrigger data-testid="input-position">
                  <SelectValue placeholder="Görev seçiniz" />
                </SelectTrigger>
                <SelectContent>
                  {positions.map((pos) => (
                    <SelectItem key={pos.id} value={pos.id}>{pos.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="branch_id">Şube *</Label>
              <Select value={formData.branch_id} onValueChange={(value) => setFormData({ ...formData, branch_id: value })} required>
                <SelectTrigger data-testid="input-branch">
                  <SelectValue placeholder="Şube seçiniz" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="starting_salary">Başlangıç Maaşı</Label>
              <Input
                id="starting_salary"
                type="number"
                value={formData.starting_salary}
                onChange={(e) => setFormData({ ...formData, starting_salary: e.target.value })}
                data-testid="input-starting-salary"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contract_type">Sözleşme Tipi</Label>
              <Select value={formData.contract_type} onValueChange={(value) => setFormData({ ...formData, contract_type: value })}>
                <SelectTrigger data-testid="input-contract-type">
                  <SelectValue placeholder="Seçiniz" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="belirsiz">Belirsiz Süreli</SelectItem>
                  <SelectItem value="belirli">Belirli Süreli</SelectItem>
                  <SelectItem value="part-time">Part-Time</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Banka ve Sigorta Bilgileri */}
            <div className="col-span-2 mt-4">
              <h3 className="text-sm font-semibold mb-2">Banka ve Sigorta Bilgileri</h3>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bank_name">Banka Adı</Label>
              <Input
                id="bank_name"
                value={formData.bank_name}
                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                placeholder="Örn: Ziraat Bankası"
                data-testid="input-bank-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="iban">IBAN</Label>
              <Input
                id="iban"
                value={formData.iban}
                onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                placeholder="TR00 0000 0000 0000 0000 0000 00"
                data-testid="input-iban"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="insurance_number">Sigorta Numarası</Label>
              <Input
                id="insurance_number"
                value={formData.insurance_number}
                onChange={(e) => setFormData({ ...formData, insurance_number: e.target.value })}
                placeholder="SGK Sicil No"
                data-testid="input-insurance-number"
              />
            </div>

            {/* Bonus Info */}
            <div className="col-span-2 mt-4">
              <h3 className="text-sm font-semibold mb-2">Prim Bilgileri</h3>
            </div>
            <div className="col-span-2 space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="has_bonus"
                  checked={formData.has_bonus}
                  onChange={(e) => setFormData({ ...formData, has_bonus: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="has_bonus">Prim var</Label>
              </div>
            </div>
            {formData.has_bonus && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="bonus_type">Prim Tipi</Label>
                  <Select value={formData.bonus_type} onValueChange={(value) => setFormData({ ...formData, bonus_type: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Prim tipi seçiniz" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="performans">Performans</SelectItem>
                      <SelectItem value="satış">Satış</SelectItem>
                      <SelectItem value="üretim">Üretim</SelectItem>
                      <SelectItem value="diğer">Diğer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="bonus_description">Prim Açıklaması</Label>
                  <Input
                    id="bonus_description"
                    value={formData.bonus_description}
                    onChange={(e) => setFormData({ ...formData, bonus_description: e.target.value })}
                    placeholder="Prim detaylarını yazınız..."
                  />
                </div>
              </>
            )}
            
            {/* Additional Supports/Benefits */}
            <div className="col-span-2 mt-4">
              <h3 className="text-sm font-semibold mb-2">Ek Yardımlar</h3>
            </div>
            
            {/* Yemek Kartı */}
            <div className="col-span-2 space-y-2">
              <div className="flex items-center gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="support_yemek"
                    checked={selectedSupports.yemek_karti.enabled}
                    onChange={(e) => setSelectedSupports({
                      ...selectedSupports,
                      yemek_karti: { ...selectedSupports.yemek_karti, enabled: e.target.checked }
                    })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="support_yemek">Yemek Kartı</Label>
                </div>
                {selectedSupports.yemek_karti.enabled && (
                  <Input
                    type="number"
                    placeholder="Tutar (TL)"
                    value={selectedSupports.yemek_karti.amount}
                    onChange={(e) => setSelectedSupports({
                      ...selectedSupports,
                      yemek_karti: { ...selectedSupports.yemek_karti, amount: e.target.value }
                    })}
                    className="w-40"
                  />
                )}
              </div>
            </div>
            
            {/* Yol Parası */}
            <div className="col-span-2 space-y-2">
              <div className="flex items-center gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="support_yol"
                    checked={selectedSupports.yol_parasi.enabled}
                    onChange={(e) => setSelectedSupports({
                      ...selectedSupports,
                      yol_parasi: { ...selectedSupports.yol_parasi, enabled: e.target.checked }
                    })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="support_yol">Yol Parası</Label>
                </div>
                {selectedSupports.yol_parasi.enabled && (
                  <Input
                    type="number"
                    placeholder="Tutar (TL)"
                    value={selectedSupports.yol_parasi.amount}
                    onChange={(e) => setSelectedSupports({
                      ...selectedSupports,
                      yol_parasi: { ...selectedSupports.yol_parasi, amount: e.target.value }
                    })}
                    className="w-40"
                  />
                )}
              </div>
            </div>
            
            {/* Sağlık Sigortası */}
            <div className="col-span-2 space-y-2">
              <div className="flex items-center gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="support_saglik"
                    checked={selectedSupports.saglik_sigortasi.enabled}
                    onChange={(e) => setSelectedSupports({
                      ...selectedSupports,
                      saglik_sigortasi: { ...selectedSupports.saglik_sigortasi, enabled: e.target.checked }
                    })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="support_saglik">Sağlık Sigortası</Label>
                </div>
                {selectedSupports.saglik_sigortasi.enabled && (
                  <Input
                    type="number"
                    placeholder="Tutar (TL)"
                    value={selectedSupports.saglik_sigortasi.amount}
                    onChange={(e) => setSelectedSupports({
                      ...selectedSupports,
                      saglik_sigortasi: { ...selectedSupports.saglik_sigortasi, amount: e.target.value }
                    })}
                    className="w-40"
                  />
                )}
              </div>
            </div>
            
            {/* Documents Section */}
            <div className="col-span-2 mt-4">
              <h3 className="text-sm font-semibold mb-2">Özlük Dosyaları</h3>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">
                Çalışana ait belgeleri yükleyin (Sonradan da eklenebilir)
              </p>
            </div>
            <div className="col-span-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  toast.info('Önce çalışanı kaydedin, sonra belgeler eklenebilir');
                }}
                className="w-full"
              >
                <FileText className="h-4 w-4 mr-2" />
                Belge Ekle (Kayıt Sonrası)
              </Button>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 text-center">
                Not: Belgeler çalışan kaydedildikten sonra eklenebilir
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setShowAddModal(false); }}>İptal</Button>
            <Button type="submit" className="bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]" data-testid="submit-add-employee">
              Ekle
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddEmployeeModal;
